/**
 * Kinn Kleanbowl Estimator - Main Application for Shopify
 * Handles form submission, validation, and results display
 */

class EstimatorApp {
    constructor() {
        this.estimator = new KleanbowlEstimator();
        this.renderer = new EstimatorResultsRenderer(this.estimator);
        this.isCalculating = false;
        
        this.init();
    }

    /**
     * Initialize the application
     */
    init() {
        this.bindEvents();
        this.setupLoadingStates();
    }

    /**
     * Bind form events
     */
    bindEvents() {
        const form = document.getElementById('kleanbowl-estimator-form');
        if (form) {
            form.addEventListener('submit', (e) => this.handleFormSubmit(e));
        }

        // Add real-time validation
        this.setupRealTimeValidation();
    }

    /**
     * Setup real-time form validation
     */
    setupRealTimeValidation() {
        const inputs = [
            'estimator-occupancy',
            'estimator-stay',
            'estimator-wage'
        ];

        inputs.forEach(inputId => {
            const input = document.getElementById(inputId);
            if (input) {
                input.addEventListener('input', () => this.validateInput(input));
                input.addEventListener('blur', () => this.validateInput(input));
            }
        });
    }

    /**
     * Validate individual input
     */
    validateInput(input) {
        const value = input.value.trim();
        const isValid = this.isPositiveNumber(value);
        
        // Remove existing validation classes
        input.classList.remove('is-valid', 'is-invalid');
        
        // Add appropriate class
        if (value && !isValid) {
            input.classList.add('is-invalid');
            this.showInputError(input, 'Please enter a positive number');
        } else if (value && isValid) {
            input.classList.add('is-valid');
            this.clearInputError(input);
        } else {
            this.clearInputError(input);
        }
    }

    /**
     * Show input validation error
     */
    showInputError(input, message) {
        let errorElement = input.parentNode.querySelector('.invalid-feedback');
        if (!errorElement) {
            errorElement = document.createElement('div');
            errorElement.className = 'invalid-feedback';
            input.parentNode.appendChild(errorElement);
        }
        errorElement.textContent = message;
    }

    /**
     * Clear input validation error
     */
    clearInputError(input) {
        const errorElement = input.parentNode.querySelector('.invalid-feedback');
        if (errorElement) {
            errorElement.textContent = '';
        }
    }

    /**
     * Check if value is a positive number
     */
    isPositiveNumber(value) {
        return /^[+]?\d*\.?\d+$/.test(value) && parseFloat(value) > 0;
    }

    /**
     * Handle form submission
     */
    async handleFormSubmit(event) {
        event.preventDefault();
        
        if (this.isCalculating) {
            return;
        }

        try {
            this.isCalculating = true;
            this.showLoadingState();

            // Get form data
            const formData = this.getFormData();
            
            // Validate inputs
            if (!this.validateFormData(formData)) {
                this.hideLoadingState();
                return;
            }

            // Calculate results
            const results = this.estimator.calculate(formData);
            
            // Show results
            await this.displayResults(results);
            
        } catch (error) {
            this.handleError(error);
        } finally {
            this.isCalculating = false;
            this.hideLoadingState();
        }
    }

    /**
     * Get form data
     */
    getFormData() {
        return {
            hourlyWage: parseFloat(document.getElementById('estimator-wage').value),
            occupancy: parseFloat(document.getElementById('estimator-occupancy').value),
            savingsFor: document.querySelector('input[name="savings-for"]:checked').value,
            stayDays: parseFloat(document.getElementById('estimator-stay').value),
            cleaningTime: parseFloat(document.getElementById('estimator-cleaning').value)
        };
    }

    /**
     * Validate form data
     */
    validateFormData(formData) {
        const errors = [];

        if (!this.isPositiveNumber(formData.hourlyWage)) {
            errors.push('Please enter a valid hourly wage');
        }

        if (!this.isPositiveNumber(formData.occupancy)) {
            errors.push('Please enter a valid number of guests');
        }

        if (!this.isPositiveNumber(formData.stayDays)) {
            errors.push('Please enter a valid number of stay days');
        }

        if (!formData.savingsFor) {
            errors.push('Please select a savings option');
        }

        if (errors.length > 0) {
            this.showValidationErrors(errors);
            return false;
        }

        this.clearValidationErrors();
        return true;
    }

    /**
     * Show validation errors
     */
    showValidationErrors(errors) {
        let errorContainer = document.getElementById('validation-errors');
        if (!errorContainer) {
            errorContainer = document.createElement('div');
            errorContainer.id = 'validation-errors';
            errorContainer.className = 'alert alert-danger';
            
            const form = document.getElementById('kleanbowl-estimator-form');
            form.parentNode.insertBefore(errorContainer, form);
        }

        errorContainer.innerHTML = `
            <strong>Please correct the following errors:</strong>
            <ul class="mb-0 mt-2">
                ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
        `;

        errorContainer.scrollIntoView({ behavior: 'smooth' });
    }

    /**
     * Clear validation errors
     */
    clearValidationErrors() {
        const errorContainer = document.getElementById('validation-errors');
        if (errorContainer) {
            errorContainer.remove();
        }
    }

    /**
     * Show loading state
     */
    showLoadingState() {
        const submitButton = document.getElementById('estimator-submit');
        if (submitButton) {
            submitButton.disabled = true;
            submitButton.innerHTML = `
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Calculating...
            `;
        }
    }

    /**
     * Hide loading state
     */
    hideLoadingState() {
        const submitButton = document.getElementById('estimator-submit');
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.innerHTML = 'Calculate Savings';
        }
    }

    /**
     * Setup loading states
     */
    setupLoadingStates() {
        // Bootstrap handles most of the styling
        // Add any custom styles if needed
    }

    /**
     * Display results
     */
    async displayResults(results) {
        // Hide any existing results
        this.renderer.hideResults();
        
        // Add small delay for better UX
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Show new results
        this.renderer.showResults(results, 'estimator-form-container');
        
        // Track analytics if available
        this.trackCalculation(results);
    }

    /**
     * Handle errors
     */
    handleError(error) {
        console.error('Estimator error:', error);
        
        this.showErrorMessage(
            'An error occurred while calculating your savings. Please check your inputs and try again.'
        );
    }

    /**
     * Show error message
     */
    showErrorMessage(message) {
        let errorContainer = document.getElementById('calculation-error');
        if (!errorContainer) {
            errorContainer = document.createElement('div');
            errorContainer.id = 'calculation-error';
            errorContainer.className = 'alert alert-danger';
            
            const form = document.getElementById('kleanbowl-estimator-form');
            form.parentNode.insertBefore(errorContainer, form.nextSibling);
        }

        errorContainer.innerHTML = `<strong>Error:</strong> ${message}`;
        errorContainer.scrollIntoView({ behavior: 'smooth' });

        // Auto-hide after 5 seconds
        setTimeout(() => {
            if (errorContainer.parentNode) {
                errorContainer.remove();
            }
        }, 5000);
    }

    /**
     * Track calculation for analytics
     */
    trackCalculation(results) {
        // Shopify Analytics tracking
        if (typeof analytics !== 'undefined') {
            analytics.track('Kleanbowl Estimator Calculation', {
                savings_for: results.inputs.savingsFor,
                occupancy: results.inputs.occupancy,
                total_savings: results.combined?.totalSavingsScenarioA || 0
            });
        }

        // Google Analytics tracking if available
        if (typeof gtag !== 'undefined') {
            gtag('event', 'estimator_calculation', {
                'event_category': 'Estimator',
                'event_label': results.inputs.savingsFor,
                'value': Math.round(results.combined?.totalSavingsScenarioA || 0)
            });
        }

        // Facebook Pixel tracking if available
        if (typeof fbq !== 'undefined') {
            fbq('track', 'Lead', {
                content_name: 'Kleanbowl Estimator',
                content_category: 'Calculator'
            });
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Only initialize if we're on the estimator page
    if (document.getElementById('kleanbowl-estimator-form')) {
        window.estimatorApp = new EstimatorApp();
    }
});

// Export for use
window.EstimatorApp = EstimatorApp;